import "chart.js/auto";

import { useEffect } from "react";

import { postTrackingData } from "@/api/endpoints/track";
import SettingsIcon from "@/assets/icons/cog.svg";
import DotsHorizontal from "@/assets/icons/dots-horizontal.svg";
import ExclamationIcon from "@/assets/icons/exclamation.svg";
import Spinner from "@/components/Spinner";
import useHash from "@/hooks/useHash";
import { cx } from "@/utils";
import { widgetTitle } from "@/views/reports/widgets/helpers";
import AccessControl from "@components/AccessControl";
import Typography from "@components/Typography";

import styles from "./BaseWidget.module.css";

export const BaseWidget = (props) => {
  const {
    className,
    extraHeader,
    mirrored,
    isCompact,
    editable,
    children,
    data,
    description,
    metric,
    hasData,
    showIsProcessingInfo,
    onEditWidgetButtonClick,
    error,
    hideOverflow,
    toolRef,
    tight,
    containerClassName,
    tools,
    column,
  } = props;

  const shouldEditOnWidgetClick = props.editable && isCompact;
  const isProcessing = !!data?.some((s) => s.meta?.is_processing);

  const hash = useHash();

  useEffect(() => {
    if (hash === metric && hasData)
      window.scrollTo(0, document.getElementById(metric).offsetTop - 100);
  }, [hash, metric, hasData]);

  return (
    <div
      id={metric}
      className={cx(
        styles.root,
        {
          [styles.compact]: isCompact,
          [styles.editable]: shouldEditOnWidgetClick,
          [styles.mirrored]: mirrored,
        },
        className
      )}
      onClick={
        shouldEditOnWidgetClick
          ? () => {
              postTrackingData("Edit report widget", {});
              onEditWidgetButtonClick();
            }
          : null
      }
    >
      <header className={styles.header}>
        <Typography margin="none" component="h5" className={styles.title}>
          {widgetTitle(props)}
          {extraHeader && extraHeader}
        </Typography>
        {description && (
          <Typography margin="none" className={styles.description}>
            {description}
          </Typography>
        )}
        {tools || toolRef ? (
          <div className={styles.edit} ref={toolRef}>
            {tools}
          </div>
        ) : (
          editable && (
            <AccessControl>
              <DotsHorizontal
                width="1.5rem"
                className={styles.edit}
                onClick={() => {
                  postTrackingData("Edit report widget", {});
                  onEditWidgetButtonClick();
                }}
              />
            </AccessControl>
          )
        )}
      </header>

      <main
        className={cx(
          styles.main,
          {
            [styles.hideOverflow]: hideOverflow,
            [styles.tight]: tight,
          },
          containerClassName,
          "widget-init" // Used for pdf:s
        )}
        data-metric={metric}
      >
        {data && !hasData && isProcessing && showIsProcessingInfo ? (
          <div className={styles.noData} data-testid="widget-is-processing">
            <SettingsIcon width="24" />
            {L.REPORTS_WIDGET_WORKING_LABEL}
          </div>
        ) : data && hasData ? (
          <div
            data-testid="widget-data-wrapper"
            className={cx(
              styles.wrapper,
              { [styles.column]: column },
              containerClassName
            )}
          >
            {children}
          </div>
        ) : data && !hasData ? (
          <div className={styles.noData} data-testid="widget-no-data">
            {L.REPORTS_WIDGET_NO_DATA_LABEL}
          </div>
        ) : error ? (
          <div className={styles.error} data-testid="widget-error">
            <ExclamationIcon width="24" />
            {L.REPORTS_WIDGET_ERROR_LABEL}
          </div>
        ) : (
          <div className={styles.loading}>
            <Spinner
              className="widget-loading" // This is used solely for waiting in the PDF generation
            />
          </div>
        )}
      </main>

      {data && hasData && isProcessing && showIsProcessingInfo && (
        <footer>
          <Typography variant="caption" align="center" margin="none">
            {L.REPORTS_WIDGET_IS_PROCESSING_FOOTER}
          </Typography>
        </footer>
      )}
    </div>
  );
};
